import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useSaveBowtieMutation, useUpdateBowtieMutation } from '../api/enhanced/enhanced-bowtie-api';
import { type BowtieDto, useUpdateGeneratedDiagramFiltersMutation } from '../api/generated/bowtie-api';
import { useDiagramContext } from '../context/diagram.context';
import { updateToast } from '../helpers/toast-util';
import { useAppSelector } from '../redux/hooks';
import { selectFilter } from '../redux/slices/diagram';

/**
 * Custom hook for handling the saving and updating of bow-tie diagrams.
 *
 * @param bowtieDto - Optional BowtieDto object containing the diagram data to be saved
 * @param mueLabel - Optional string representing the Material Unwanted Event label
 *
 * @returns An object containing:
 * - loading: boolean indicating if save/update operation is in progress
 * - label: string displaying either "Update" or "Save" based on whether diagram exists
 * - handleClick: async function that handles the save/update operation
 *
 * @remarks
 * - For flow diagrams, both mueLabel and bowtieDto are required
 * - Shows toast notifications for success/error states
 * - Redirects to new URL with diagramId after successful save of new diagram
 * - Uses existing diagramId from URL params for updates
 * - Will show error toast if save/update operation fails
 */
const useSaveDiagram = (bowtieDto?: BowtieDto, mueLabel?: string) => {
  const [loading, setLoading] = useState(false);
  const { diagramId } = useDiagramContext();
  const diagramFilter = useAppSelector(selectFilter);

  const [saveBowtie] = useSaveBowtieMutation();
  const [updateBowtie] = useUpdateBowtieMutation();
  const [updateGeneratedDiagramFilters] = useUpdateGeneratedDiagramFiltersMutation();

  const handleClick = useCallback(async () => {
    if (!mueLabel || !bowtieDto) {
      toast.error('Please specify the Material Unwanted Event', { autoClose: 2000 });
      return;
    }

    setLoading(true);
    const toastId = toast.loading('Saving the diagram...');

    try {
      const result = diagramId
        ? await updateBowtie({ bowtieId: diagramId, bowtieDto: bowtieDto! }).unwrap()
        : await saveBowtie({ bowtieDto: bowtieDto! }).unwrap();

      if (!diagramId) {
        const newDiagramId = result as string;

        // save the filters
        await updateGeneratedDiagramFilters({
          id: newDiagramId,
          diagramFilterDto: { displayFilter: { ...diagramFilter } },
        });

        updateToast(toastId, 'Diagram saved successfully', 'success');

        const params = new URLSearchParams({ diagramId: newDiagramId });

        setTimeout(
          () => window.location.replace(`${window.location.origin}${window.location.pathname}?${params}`),
          2000
        );
      } else {
        updateToast(toastId, 'Diagram updated successfully', 'success');
      }
    } catch {
      updateToast(toastId, 'Unable to save the diagram', 'error');
    } finally {
      setLoading(false);
    }
  }, [bowtieDto, mueLabel]);

  return {
    loading,
    label: diagramId ? 'Update' : 'Save',
    handleClick,
    diagramId,
  };
};

export default useSaveDiagram;
